// FontSwapModal — Jun 15.
//
// Presents the curated 40-font Google Fonts catalog and lets the user pick
// a replacement for one of their brand's detected fonts. Category filter
// pills at the top let them narrow Serif / Sans / Display / etc. The chosen
// font is previewed inline (each option name renders in its own family) so
// the user sees what they're picking BEFORE committing.
//
// We don't dynamically load woff2s for the catalog options here — too
// expensive (~38 fonts × 2 weights). Instead we rely on Google Fonts
// fallback chains + the user's already-cached fonts; the actual rendered
// brand outputs load the selected font via @fontsource at build time.
//
// Cost: 1 catalog fetch on first open per session (cached). Cheap.

import { useEffect, useMemo, useState } from 'react';
import {
  type GoogleFontCatalogEntry,
  getGoogleFontsCatalog,
} from '@/lib/api';

interface FontSwapModalProps {
  detected: string;            // original font from website scrape
  currentSubstitute: string;   // currently-applied substitute (highlighted in list)
  onPick: (family: string) => void;
  onClose: () => void;
}

type CategoryFilter = 'all' | GoogleFontCatalogEntry['category'];

const CATEGORY_LABELS: Record<CategoryFilter, string> = {
  all: 'All',
  sans: 'Sans',
  serif: 'Serif',
  display: 'Display',
  handwriting: 'Handwriting',
  monospace: 'Monospace',
};

const CATEGORY_FILTERS: CategoryFilter[] = [
  'all', 'sans', 'serif', 'display', 'handwriting', 'monospace',
];

export default function FontSwapModal({
  detected,
  currentSubstitute,
  onPick,
  onClose,
}: FontSwapModalProps) {
  const [catalog, setCatalog] = useState<GoogleFontCatalogEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [category, setCategory] = useState<CategoryFilter>('all');
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const c = await getGoogleFontsCatalog();
        if (!cancelled) setCatalog(c);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load fonts catalog.');
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Escape key closes — quick UX win, expected modal behavior.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const filtered = useMemo(() => {
    if (!catalog) return [];
    let list = catalog;
    if (category !== 'all') {
      list = list.filter((f) => f.category === category);
    }
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((f) => f.family.toLowerCase().includes(q));
    }
    return list;
  }, [catalog, category, query]);

  return (
    <div
      // Backdrop + click-outside-close. The inner card stops propagation so
      // clicks inside don't bubble to the backdrop.
      onClick={onClose}
      className="
        fixed inset-0 z-50 flex items-center justify-center
        bg-dark/40 backdrop-blur-sm px-4
      "
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="
          w-full max-w-2xl max-h-[85vh] flex flex-col
          bg-white rounded-2xl shadow-2xl border border-surface-dark/30 overflow-hidden
        "
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-surface-dark/20 flex items-start justify-between gap-4">
          <div>
            <h2 className="font-display font-bold text-dark text-lg mb-0.5">
              Pick a Google Font
            </h2>
            <p className="text-xs text-dark-muted">
              Replacing the substitute for{' '}
              <span className="italic">{detected}</span>. We can only render
              with Google Fonts — pick the closest match to your brand voice.
            </p>
          </div>
          <button
            onClick={onClose}
            className="
              w-8 h-8 rounded-full text-dark-muted hover:text-dark hover:bg-surface
              flex items-center justify-center transition-colors cursor-pointer flex-shrink-0
            "
            aria-label="Close"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Filters */}
        <div className="px-6 py-3 border-b border-surface-dark/20 space-y-2.5">
          <div className="flex items-center gap-1.5 flex-wrap">
            {CATEGORY_FILTERS.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={`
                  text-xs font-semibold px-3 py-1.5 rounded-full transition-colors cursor-pointer
                  ${category === c
                    ? 'bg-primary text-white'
                    : 'bg-surface text-dark-muted hover:bg-surface-dark/40'}
                `}
              >
                {CATEGORY_LABELS[c]}
              </button>
            ))}
          </div>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search fonts…"
            className="
              w-full px-4 py-2 rounded-full bg-surface border border-surface-dark/40
              text-sm text-dark placeholder:text-dark-muted/60
              focus:outline-none focus:border-primary/40
            "
          />
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-3 py-2">
          {error ? (
            <div className="text-sm text-red-600 px-3 py-6 text-center">
              {error}
            </div>
          ) : !catalog ? (
            <div className="flex items-center justify-center py-10 text-dark-muted text-sm">
              <i className="ri-loader-4-line animate-spin text-xl mr-2" />
              Loading fonts…
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-sm text-dark-muted px-3 py-6 text-center">
              No fonts match — try a different category or clear the search.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {filtered.map((f) => (
                <FontOption
                  key={f.family}
                  entry={f}
                  selected={f.family === currentSubstitute}
                  onClick={() => onPick(f.family)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function FontOption({
  entry,
  selected,
  onClick,
}: {
  entry: GoogleFontCatalogEntry;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`
        text-left px-4 py-2.5 rounded-xl transition-colors cursor-pointer
        flex items-center justify-between gap-3
        ${selected
          ? 'bg-primary/10 border-2 border-primary/40'
          : 'bg-white border-2 border-transparent hover:bg-surface'}
      `}
    >
      <div className="flex-1 min-w-0">
        <div
          className="text-base text-dark truncate"
          style={{ fontFamily: `"${entry.family}", ${entry.category === 'serif' ? 'serif' : 'sans-serif'}` }}
        >
          {entry.family}
        </div>
        <div className="text-[10px] uppercase tracking-wider text-dark-muted/70 mt-0.5">
          {entry.category} · {entry.classification}
        </div>
      </div>
      {selected && (
        <i className="ri-check-line text-primary text-lg flex-shrink-0" />
      )}
    </button>
  );
}
